import type { AxialCoordinate } from '@drawn-of-war/shared';

/**
 * TASK-SMOOTH-002: Linear Interpolation Math
 *
 * Pure functions for client-side entity interpolation.
 * Used to smoothly render unit movement between server state updates.
 *
 * Reference: Gabriel Gambetta - Entity Interpolation
 * https://www.gabrielgambetta.com/entity-interpolation.html
 */

/**
 * Linear interpolation between two values
 *
 * @param start - Starting value
 * @param end - Ending value
 * @param alpha - Progress (0 = start, 1 = end)
 * @returns Interpolated value
 */
export function lerp(start: number, end: number, alpha: number): number {
  return start + (end - start) * alpha;
}

/**
 * Calculate interpolation alpha (progress) between two timestamps
 *
 * @param renderTime - Current render time
 * @param olderTime - Older state timestamp
 * @param newerTime - Newer state timestamp
 * @returns Alpha value clamped to [0, 1]
 */
export function calculateAlpha(
  renderTime: number,
  olderTime: number,
  newerTime: number
): number {
  // Avoid division by zero
  if (newerTime === olderTime) {
    return 1.0;
  }

  const alpha = (renderTime - olderTime) / (newerTime - olderTime);

  // Clamp to [0, 1]
  return Math.max(0, Math.min(1, alpha));
}

/**
 * Interpolate between two hex positions
 *
 * @param oldPos - Starting hex position
 * @param newPos - Ending hex position
 * @param alpha - Interpolation progress (0 to 1)
 * @returns Interpolated hex position
 */
export function interpolateHexPosition(
  oldPos: AxialCoordinate,
  newPos: AxialCoordinate,
  alpha: number
): AxialCoordinate {
  return {
    q: lerp(oldPos.q, newPos.q, alpha),
    r: lerp(oldPos.r, newPos.r, alpha)
  };
}

/**
 * Detect if position change is a teleport (not normal movement)
 * Used to skip interpolation for large jumps
 *
 * @param oldPos - Old position
 * @param newPos - New position
 * @param maxDistance - Max distance for normal movement (default: 2 hexes)
 * @returns True if this is likely a teleport
 */
export function isTeleport(
  oldPos: AxialCoordinate,
  newPos: AxialCoordinate,
  maxDistance: number = 2
): boolean {
  // Hex distance calculation (axial coordinates)
  const dq = Math.abs(newPos.q - oldPos.q);
  const dr = Math.abs(newPos.r - oldPos.r);
  const distance = Math.max(dq, dr, Math.abs(dq - dr));

  return distance > maxDistance;
}
